/**
 * 세션 분석 — 세션 목록의 열 · 검색 · 정렬 (2026-10-01).
 *
 * 열 정의가 한 곳이어야 머리글 · 정렬 · 검색 · 셀 클릭(→ 검색어) 이 같은 값을 본다. 예전에는 검색이 다섯 칸
 * (사용자·머신·프로그램·모듈·SQL_ID)만 봐서, 대기 이벤트나 SID 를 검색어로 넣어도 걸리지 않았다.
 *
 * `text` 는 화면에 보이는 글자 그대로다 — 셀을 눌러 검색어로 넣은 값이 그 칸에서 반드시 다시 걸려야 한다.
 */

/** 열 — key 는 정렬·고정의 키. num 이면 숫자로 정렬한다(문자로 견주면 1000 이 200 앞에 온다). */
export const SESSION_COLUMNS = [
    { key: 'sid',          text: s => `${s.sid},${s.serial}`, sortValue: s => s.sid, num: true },
    { key: 'user',         text: s => s.username || '' },
    { key: 'status',       text: s => s.status || '' },
    { key: 'osuser',       text: s => s.osuser || '' },
    { key: 'machine',      text: s => s.machine || '' },
    { key: 'program',      text: s => s.program || '' },
    { key: 'module',       text: s => s.module || '' },
    { key: 'action',       text: s => s.action || '' },
    { key: 'service',      text: s => s.service || '' },
    { key: 'event',        text: s => s.event || '' },
    { key: 'waitClass',    text: s => s.waitClass || '' },
    { key: 'secondsInWait', text: s => (s.secondsInWait == null ? '' : String(s.secondsInWait)), sortValue: s => s.secondsInWait, num: true },
    { key: 'elapsed',      text: s => (s.lastCallEt == null ? '' : String(s.lastCallEt)), sortValue: s => s.lastCallEt, num: true },
    { key: 'sqlId',        text: s => s.sqlId || '' },
    { key: 'sqlExecStart', text: s => (s.sqlExecStart ? String(s.sqlExecStart).replace('T', ' ') : '') },
    { key: 'prevSqlId',    text: s => s.prevSqlId || '' },
    { key: 'blocking',     text: s => (s.blockingSession == null ? '' : String(s.blockingSession)), sortValue: s => s.blockingSession, num: true },
    { key: 'logonTime',    text: s => (s.logonTime ? String(s.logonTime).replace('T', ' ') : '') },
];

const COL_BY_KEY = new Map(SESSION_COLUMNS.map(c => [c.key, c]));

/** RAC 노드 목록 — 둘 이상일 때만 리본을 세운다(누를 것이 없는 리본은 소음이다) */
export function sessionInstances(sessions) {
    return [...new Set((sessions || []).map(s => s.instId ?? 1))].sort((a, b) => a - b);
}

/**
 * 거르기 — 상태 · 노드 · 검색어(모든 열의 보이는 글자, 대소문자 무시).
 * 셀을 눌러 넣은 값(예: `145,8801`, `gc cr request`)이 그 칸에서 다시 걸린다.
 */
export function filterSessions(sessions, { status, inst, q } = {}) {
    const needle = String(q || '').trim().toLowerCase();
    return (sessions || []).filter(s => {
        if (status && s.status !== status) return false;
        if (inst != null && (s.instId ?? 1) !== inst) return false;
        if (!needle) return true;
        return SESSION_COLUMNS.some(c => c.text(s).toLowerCase().includes(needle));
    });
}

/** 정렬 — sort = { col, dir } | null. 빈 값은 방향과 무관하게 맨 뒤(위에 빈 줄이 몰리면 읽을 게 없다) */
export function sortSessions(sessions, sort) {
    const list = sessions || [];
    const col = sort && COL_BY_KEY.get(sort.col);
    if (!col) return list;
    const get = col.sortValue || col.text;
    const sign = sort.dir === 'desc' ? -1 : 1;
    const empty = (v) => v == null || v === '';
    return list
        .map((s, i) => ({ s, i, v: get(s) }))
        .sort((a, b) => {
            if (empty(a.v) && empty(b.v)) return a.i - b.i;
            if (empty(a.v)) return 1;
            if (empty(b.v)) return -1;
            const d = col.num ? Number(a.v) - Number(b.v) : String(a.v).localeCompare(String(b.v));
            return d !== 0 ? sign * d : a.i - b.i;      // 같으면 원래 순서 — 새로고침마다 줄이 춤추지 않게
        })
        .map(x => x.s);
}
